import { useAuth } from "@hooks/useAuth";
import { Profile } from "types";
import { authClient } from "@utils/authClient";
import { supabase } from "@utils/serverClient";
import { useQuery } from "@tanstack/react-query";
import { serverEndpoints } from "@utils/serverEndpoints";

export function useImpersonation() {
    const { user } = useAuth();

    const { data: users } = useQuery<{ data: Profile[] }>({
        queryKey: ["users"],
        enabled: user?.system_role === "admin",
        queryFn: async () => {
            const { data, error } = await supabase
                .from("profile")
                .select("*")
                .neq("id", user?.id);

            if (error) throw error;
            return { data };
        },
    });

    const isImpersonating = !!localStorage.getItem("original_uid");

    async function _impersonate(targetUserId: string) {
        const token = await authClient.getAccessToken();
        if (!token) return;

        const response = await fetch(serverEndpoints.userImpersonation, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ targetUserId }),
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error("Failed to impersonate user");
        }

        const { token: impersonationToken } = await response.json();

        // Verify the impersonation token through the server and set the new session
        const { error } = await authClient.verifyOtp(
            "", // email not needed for token_hash verification
            impersonationToken,
        );
        if (error) {
            throw new Error("Failed to impersonate user");
        }
    }

    const impersonateUser = async (targetUser: { id: string }) => {
        await _impersonate(targetUser.id);

        // Store the original token before impersonating if not already impersonating
        if(!isImpersonating)
            localStorage.setItem("original_uid", user!.id);
    };

    const endImpersonation = async () => {
        await _impersonate(localStorage.getItem("original_uid")!);

        localStorage.removeItem("original_uid");
    };

    return {
        users: users?.data ?? [],
        impersonateUser,
        isImpersonating,
        endImpersonation,
    };
}